import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Grid3X3, LayoutGrid, List, Star, X,
  SortAsc, RotateCcw, Filter, Calendar, Dog, Users,
  Camera, User, Clock,
} from 'lucide-react';
import { Button } from '../ui/button';

const VIEW_MODES = [
  { key: 'grid', icon: Grid3X3 },
  { key: 'masonry', icon: LayoutGrid },
  { key: 'list', icon: List },
];

const PAGE_SIZES = [20, 50, 100, 200];

const HOUR_BUTTONS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];

export default function SportGalleryToolbar({
  viewMode,
  onViewModeChange,
  sortBy,
  onSortChange,
  filterRatings = [],
  onRatingsChange,
  filenameSearch = '',
  onFilenameSearchChange,
  filterDogId = '',
  onDogIdChange,
  filterTeamId = '',
  onTeamIdChange,
  dateStart = '',
  onDateStartChange,
  dateEnd = '',
  onDateEndChange,
  pageSize = 50,
  onPageSizeChange,
  // Event filter (season view)
  events = [],
  filterEventId = '',
  onEventIdChange,
  // Photographer
  photographers = [],
  filterPhotographerId = '',
  onPhotographerIdChange,
  // Competitor
  competitors = [],
  filterCompetitorId = '',
  onCompetitorIdChange,
  // Camera
  cameras = [],
  filterCamera = '',
  onCameraChange,
  // Burst type
  filterBurstType = '',
  onBurstTypeChange,
  // Time of day
  filterTimeStart = '',
  onTimeStartChange,
  filterTimeEnd = '',
  onTimeEndChange,
  // Competition day (dates of selected event)
  competitionDays = [],
  filterCompetitionDay = '',
  onCompetitionDayChange,
  total = 0,
  dogs = [],
  teams = [],
  language = 'pl',
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filenameInput, setFilenameInput] = useState(filenameSearch);
  const [dogSearch, setDogSearch] = useState('');
  const [competitorSearch, setCompetitorSearch] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilenameSearchChange?.(filenameInput);
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filenameInput]);

  const sortOptions = language === 'pl'
    ? [
        { key: 'date_desc', label: 'Najnowsze' },
        { key: 'date_asc', label: 'Najstarsze' },
        { key: 'rating', label: 'Najlepsza ocena' },
        { key: 'random', label: 'Losowo' },
        { key: 'filename', label: 'Nazwa pliku' },
      ]
    : [
        { key: 'date_desc', label: 'Newest first' },
        { key: 'date_asc', label: 'Oldest first' },
        { key: 'rating', label: 'Best rated' },
        { key: 'random', label: 'Random' },
        { key: 'filename', label: 'Filename' },
      ];

  const lbl = language === 'pl'
    ? {
        filters: 'Filtry', sort: 'Sortuj', reset: 'Reset filtrow', search: 'Szukaj pliku...',
        dateFrom: 'Data od', dateTo: 'Data do', dog: 'Pies', team: 'Druzyna',
        allDogs: 'Wszystkie psy', allTeams: 'Wszystkie druzyny', perPage: 'na strone',
        rating: 'Ocena', searchDog: 'Szukaj psa...',
        none: 'Bez oceny', allEvents: 'Wszystkie zawody',
        photographer: 'Fotograf', allPhotographers: 'Wszyscy fotografowie',
        competitor: 'Zawodnik', allCompetitors: 'Wszyscy zawodnicy',
        searchCompetitor: 'Szukaj zawodnika...', notFound: 'Nie znaleziono',
        camera: 'Kamera', allCameras: 'Wszystkie kamery',
        burstType: 'Typ serii', allTypes: 'Wszystkie', seriesOnly: 'Tylko serie', singleOnly: 'Pojedyncze',
        timeFrom: 'Godzina od', timeTo: 'Godzina do',
        timeLabel: 'Filtruj wedlug godziny',
        quickHours: 'Szybkie filtry godzinowe (kliknij wiele aby rozszerzyc zakres)',
        clearTime: 'Wyczysc',
        chooseEvent: '1. Wybierz zawody',
        chooseDay: '2. Dzien zawodow',
        allDays: 'Wszystkie dni',
        showAll: 'Pokaz wszystkie zdjecia',
        photos: 'zdjec',
      }
    : {
        filters: 'Filters', sort: 'Sort', reset: 'Reset filters', search: 'Search file...',
        dateFrom: 'Date from', dateTo: 'Date to', dog: 'Dog', team: 'Team',
        allDogs: 'All dogs', allTeams: 'All teams', perPage: 'per page',
        rating: 'Rating', searchDog: 'Search dog...',
        none: 'Unrated', allEvents: 'All events',
        photographer: 'Photographer', allPhotographers: 'All photographers',
        competitor: 'Competitor', allCompetitors: 'All competitors',
        searchCompetitor: 'Search competitor...', notFound: 'Not found',
        camera: 'Camera', allCameras: 'All cameras',
        burstType: 'Burst type', allTypes: 'All', seriesOnly: 'Series only', singleOnly: 'Single only',
        timeFrom: 'Time from', timeTo: 'Time to',
        timeLabel: 'Filter by time of day',
        quickHours: 'Quick hour filters (click multiple to extend range)',
        clearTime: 'Clear',
        chooseEvent: '1. Choose event',
        chooseDay: '2. Event day',
        allDays: 'All days',
        showAll: 'Show all photos',
        photos: 'photos',
      };

  const toggleRating = (r) => {
    if (filterRatings.includes(r)) {
      onRatingsChange?.(filterRatings.filter(x => x !== r));
    } else {
      onRatingsChange?.([...filterRatings, r]);
    }
  };

  const activeFilterCount =
    filterRatings.length +
    (filterDogId ? 1 : 0) +
    (filterTeamId ? 1 : 0) +
    (filterEventId ? 1 : 0) +
    (filterPhotographerId ? 1 : 0) +
    (filterCompetitorId ? 1 : 0) +
    (filterCamera ? 1 : 0) +
    (filterBurstType ? 1 : 0) +
    (filterTimeStart ? 1 : 0) +
    (filterTimeEnd ? 1 : 0) +
    (filterCompetitionDay ? 1 : 0) +
    (dateStart ? 1 : 0) +
    (dateEnd ? 1 : 0);

  const resetFilters = () => {
    onRatingsChange?.([]);
    setFilenameInput('');
    onFilenameSearchChange?.('');
    onDogIdChange?.('');
    onTeamIdChange?.('');
    onDateStartChange?.('');
    onDateEndChange?.('');
    onEventIdChange?.('');
    onPhotographerIdChange?.('');
    onCompetitorIdChange?.('');
    onCameraChange?.('');
    onBurstTypeChange?.('');
    onTimeStartChange?.('');
    onTimeEndChange?.('');
    onCompetitionDayChange?.('');
    setDogSearch('');
    setCompetitorSearch('');
  };

  // Filter dogs by search
  const filteredDogs = dogSearch
    ? dogs.filter(d =>
        (d.name || '').toLowerCase().includes(dogSearch.toLowerCase()) ||
        (d.breed || '').toLowerCase().includes(dogSearch.toLowerCase()) ||
        (d.team_name || '').toLowerCase().includes(dogSearch.toLowerCase())
      )
    : dogs;

  // Filter competitors by search
  const filteredCompetitors = competitorSearch
    ? competitors.filter(c => {
        const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
        return fullName.includes(competitorSearch.toLowerCase());
      })
    : competitors;

  const handleHourClick = (hour) => {
    const hourStart = `${hour.toString().padStart(2, '0')}:00`;
    const hourEnd = `${(hour + 1).toString().padStart(2, '0')}:00`;

    if (!filterTimeStart || !filterTimeEnd) {
      onTimeStartChange?.(hourStart);
      onTimeEndChange?.(hourEnd);
    } else {
      const newStart = hourStart < filterTimeStart ? hourStart : filterTimeStart;
      const newEnd = hourEnd > filterTimeEnd ? hourEnd : filterTimeEnd;
      if (newStart === filterTimeStart && newEnd === filterTimeEnd) {
        onTimeStartChange?.(hourStart);
        onTimeEndChange?.(hourEnd);
      } else {
        onTimeStartChange?.(newStart);
        onTimeEndChange?.(newEnd);
      }
    }
  };

  const isHourActive = (hour) => {
    const hourStart = `${hour.toString().padStart(2, '0')}:00`;
    return filterTimeStart && filterTimeEnd &&
      hourStart >= filterTimeStart && hourStart < filterTimeEnd;
  };

  return (
    <div className="mb-6">
      {/* ===== HEADER BAR ===== */}
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Left: hint text */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="w-4 h-4" />
            <span>{language === 'pl' ? 'Uzyj klawisza Filtry aby wyszukac zdjecia' : 'Use the Filters button to search photos'}</span>
          </div>

          {/* Right: controls */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="flex items-center border border-border rounded-md overflow-hidden">
              {VIEW_MODES.map(({ key, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => onViewModeChange?.(key)}
                  className={`p-2 transition-colors ${
                    viewMode === key
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  }`}
                  title={key}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>

            {/* Filters button */}
            <Button
              variant={filtersOpen ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFiltersOpen(v => !v)}
              className="gap-1.5"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">{lbl.filters}</span>
              {activeFilterCount > 0 && (
                <span className="bg-primary-foreground text-primary text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center ml-0.5">
                  {activeFilterCount}
                </span>
              )}
            </Button>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={e => onSortChange?.(e.target.value)}
              className="text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none cursor-pointer"
            >
              {sortOptions.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>

            {/* Page size */}
            {onPageSizeChange && (
              <select
                value={pageSize}
                onChange={e => onPageSizeChange?.(Number(e.target.value))}
                className="text-sm bg-secondary border border-border rounded-md px-2 py-1.5 outline-none cursor-pointer"
              >
                {PAGE_SIZES.map(s => (
                  <option key={s} value={s}>{s} {lbl.perPage}</option>
                ))}
              </select>
            )}

            {/* Photo count */}
            <span className="text-sm text-muted-foreground tabular-nums">
              {total} {lbl.photos}
            </span>
          </div>
        </div>

        {/* ===== FILTER PANEL ===== */}
        <AnimatePresence>
          {filtersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="pt-4 mt-4 border-t border-border space-y-4">

                {/* Filename search */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{lbl.search}</label>
                  <div className="relative max-w-sm">
                    <input
                      type="text"
                      value={filenameInput}
                      onChange={e => setFilenameInput(e.target.value)}
                      placeholder={lbl.search}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 pr-8 outline-none"
                    />
                    {filenameInput && (
                      <button
                        onClick={() => setFilenameInput('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Row 1: 8-column filter grid — ALWAYS VISIBLE */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
                  {/* Photographer */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{lbl.photographer}</label>
                    <select
                      value={filterPhotographerId}
                      onChange={e => onPhotographerIdChange?.(e.target.value)}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none cursor-pointer"
                    >
                      <option value="">{lbl.allPhotographers}</option>
                      {photographers.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Date from */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      {lbl.dateFrom} {filterCompetitionDay && <span className="text-[10px] opacity-50">(dzien)</span>}
                    </label>
                    <input
                      type="date"
                      value={dateStart}
                      onChange={e => onDateStartChange?.(e.target.value)}
                      disabled={!!filterCompetitionDay}
                      className={`w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none ${filterCompetitionDay ? 'opacity-40' : ''}`}
                    />
                  </div>

                  {/* Date to */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      {lbl.dateTo} {filterCompetitionDay && <span className="text-[10px] opacity-50">(dzien)</span>}
                    </label>
                    <input
                      type="date"
                      value={dateEnd}
                      onChange={e => onDateEndChange?.(e.target.value)}
                      disabled={!!filterCompetitionDay}
                      className={`w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none ${filterCompetitionDay ? 'opacity-40' : ''}`}
                    />
                  </div>

                  {/* Camera */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{lbl.camera}</label>
                    <select
                      value={filterCamera}
                      onChange={e => onCameraChange?.(e.target.value)}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none cursor-pointer"
                    >
                      <option value="">{lbl.allCameras}</option>
                      {cameras.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Burst type */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{lbl.burstType}</label>
                    <select
                      value={filterBurstType}
                      onChange={e => onBurstTypeChange?.(e.target.value)}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none cursor-pointer"
                    >
                      <option value="">{lbl.allTypes}</option>
                      <option value="series">{lbl.seriesOnly}</option>
                      <option value="single">{lbl.singleOnly}</option>
                    </select>
                  </div>

                  {/* Dog — search + select */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{lbl.dog}</label>
                    <input
                      type="text"
                      value={dogSearch}
                      onChange={e => setDogSearch(e.target.value)}
                      placeholder={lbl.searchDog}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none mb-1"
                    />
                    <select
                      value={filterDogId}
                      onChange={e => { onDogIdChange?.(e.target.value); setDogSearch(''); }}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none cursor-pointer"
                    >
                      <option value="">{lbl.allDogs}</option>
                      {filteredDogs.map(dog => (
                        <option key={dog.id} value={dog.id}>
                          {dog.name || `#${dog.id.slice(0, 6)}`}
                          {dog.breed && ` (${dog.breed})`}
                          {dog.team_name && ` - ${dog.team_name}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Competitor — search + select */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{lbl.competitor}</label>
                    <input
                      type="text"
                      value={competitorSearch}
                      onChange={e => setCompetitorSearch(e.target.value)}
                      placeholder={lbl.searchCompetitor}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none mb-1"
                    />
                    <select
                      value={filterCompetitorId}
                      onChange={e => { onCompetitorIdChange?.(e.target.value); setCompetitorSearch(''); }}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none cursor-pointer"
                    >
                      <option value="">{lbl.allCompetitors}</option>
                      {filteredCompetitors.map(comp => (
                        <option key={comp.id} value={comp.id}>
                          {comp.first_name} {comp.last_name}
                          {comp.team_name && ` - ${comp.team_name}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Team */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{lbl.team}</label>
                    <select
                      value={filterTeamId}
                      onChange={e => onTeamIdChange?.(e.target.value)}
                      className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none cursor-pointer"
                    >
                      <option value="">{lbl.allTeams}</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} {team.club ? `(${team.club})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Competition selector — season view (when events provided) */}
                {events.length > 0 && onEventIdChange && (
                  <div className="bg-blue-500/10 p-4 rounded-lg border-l-4 border-blue-500">
                    <label className="block text-sm font-medium text-blue-400 mb-2">
                      {lbl.chooseEvent}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      <button
                        onClick={() => { onEventIdChange?.(''); onCompetitionDayChange?.(''); }}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                          !filterEventId
                            ? 'bg-blue-500 text-white'
                            : 'bg-card hover:bg-secondary border border-border'
                        }`}
                      >
                        <div className="font-medium">{lbl.allEvents}</div>
                        <div className="text-xs opacity-75">{lbl.showAll}</div>
                      </button>
                      {events.map(ev => (
                        <button
                          key={ev.id}
                          onClick={() => { onEventIdChange?.(ev.id); onCompetitionDayChange?.(''); }}
                          className={`px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                            filterEventId === ev.id
                              ? 'bg-blue-500 text-white'
                              : 'bg-card hover:bg-secondary border border-border'
                          }`}
                        >
                          <div className="font-medium">{ev.name}</div>
                          <div className="text-xs opacity-75">
                            {ev.date_start && new Date(ev.date_start).toLocaleDateString('pl-PL')}
                            {ev.photo_count != null && ` \u2022 ${ev.photo_count} zdj.`}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Competition day — when event selected and days available */}
                {filterEventId && competitionDays.length > 0 && onCompetitionDayChange && (
                  <div className="bg-green-500/10 p-4 rounded-lg border-l-4 border-green-500">
                    <label className="block text-sm font-medium text-green-400 mb-2">
                      {lbl.chooseDay} "{events.find(e => e.id === filterEventId)?.name || ''}"
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => onCompetitionDayChange?.('')}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          !filterCompetitionDay
                            ? 'bg-green-500 text-white'
                            : 'bg-card hover:bg-secondary border border-border'
                        }`}
                      >
                        <div className="font-medium">{lbl.allDays}</div>
                      </button>
                      {competitionDays.map(day => (
                        <button
                          key={day.date}
                          onClick={() => onCompetitionDayChange?.(day.date)}
                          className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                            filterCompetitionDay === day.date
                              ? 'bg-green-500 text-white'
                              : 'bg-card hover:bg-secondary border border-border'
                          }`}
                        >
                          <div className="font-medium">{new Date(day.date).toLocaleDateString('pl-PL')}</div>
                          {day.photo_count != null && (
                            <div className="text-xs opacity-75">{day.photo_count} {lbl.photos}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time / Hour section */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-2">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {lbl.timeLabel}
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[11px] text-muted-foreground mb-1">{lbl.timeFrom}</label>
                      <input
                        type="time"
                        value={filterTimeStart}
                        onChange={e => onTimeStartChange?.(e.target.value)}
                        step="60"
                        className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-muted-foreground mb-1">{lbl.timeTo}</label>
                      <input
                        type="time"
                        value={filterTimeEnd}
                        onChange={e => onTimeEndChange?.(e.target.value)}
                        step="60"
                        className="w-full text-sm bg-secondary border border-border rounded-md px-2.5 py-1.5 outline-none"
                      />
                    </div>
                    <div className="sm:col-span-1 lg:col-span-2">
                      <label className="block text-[11px] text-muted-foreground mb-1">{lbl.quickHours}</label>
                      <div className="flex flex-wrap gap-1">
                        {HOUR_BUTTONS.map(hour => (
                          <button
                            key={hour}
                            onClick={() => handleHourClick(hour)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              isHourActive(hour)
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/80'
                            }`}
                          >
                            {hour}:00-{hour + 1}:00
                          </button>
                        ))}
                        <button
                          onClick={() => { onTimeStartChange?.(''); onTimeEndChange?.(''); }}
                          className="px-2 py-1 text-xs rounded transition-colors bg-destructive/15 text-destructive hover:bg-destructive/25"
                        >
                          {lbl.clearTime}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Star Rating */}
                {onRatingsChange && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      <Star className="w-3 h-3 inline mr-1" />
                      {lbl.rating}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {[5, 4, 3, 2, 1].map(r => (
                        <button
                          key={r}
                          onClick={() => toggleRating(r)}
                          className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border transition-all ${
                            filterRatings.includes(r)
                              ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50 shadow-sm'
                              : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 hover:bg-secondary/50'
                          }`}
                        >
                          {Array.from({ length: r }, (_, i) => (
                            <Star key={i} className={`w-3.5 h-3.5 ${filterRatings.includes(r) ? 'fill-yellow-500 text-yellow-500' : 'fill-current'}`} />
                          ))}
                        </button>
                      ))}
                      <button
                        onClick={() => toggleRating(0)}
                        className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border transition-all ${
                          filterRatings.includes(0)
                            ? 'bg-gray-500/20 text-gray-400 border-gray-500/50'
                            : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                        }`}
                      >
                        <Star className="w-3.5 h-3.5" />
                        {lbl.none}
                      </button>
                    </div>
                  </div>
                )}

                {/* Reset */}
                {activeFilterCount > 0 && (
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1.5 text-muted-foreground">
                      <RotateCcw className="w-3.5 h-3.5" />
                      {lbl.reset}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
